/*
 * CUSTOM (Schuhbeck): Hero Slider
 * Companion to sections/hero-slider.liquid. <hero-slider> crossfades the
 * pre-rendered image/card layers by toggling .is-active — no DOM is created
 * or measured here, so the server-rendered first slide is also the no-JS
 * fallback. Autoplay (data-autoplay="<seconds>") pauses on hover/focus and
 * hidden tabs, stops for good after any manual navigation, and never starts
 * for users with prefers-reduced-motion. In the theme editor, selecting a
 * slide block jumps to that slide.
 * The claim bar text (.hero-slider__claim-text) must never wrap or truncate:
 * _fitClaim() shrinks its font-size until the single line fits, re-running
 * after webfont load and on resize.
 */

if ( typeof HeroSlider !== 'function' ) {

	class HeroSlider extends HTMLElement {

		connectedCallback(){

			if ( this._initialized ) return;
			this._initialized = true;

			this._images = this.querySelectorAll('.hero-slider__image');
			this._cards = this.querySelectorAll('.hero-slider__card');
			this._dots = this.querySelectorAll('.hero-slider__dot');
			this._index = 0;
			this._timer = null;

			// claim bar: keep the text on one line at any viewport width

			this._claim = this.querySelector('.hero-slider__claim-text');

			if ( this._claim ) {
				this._fitClaim();
				this._onResize = ()=>this._fitClaim();
				window.addEventListener('resize', this._onResize);
				if ( document.fonts && document.fonts.ready ) {
					document.fonts.ready.then(()=>this._fitClaim());
				}
			}

			if ( this._cards.length < 2 ) return;

			// arrows + dots

			this.querySelectorAll('.hero-slider__arrow').forEach(arrow=>{
				arrow.addEventListener('click', ()=>{
					this._stopAutoplay();
					this.go(this._index + (arrow.dataset.dir === 'prev' ? -1 : 1));
				});
			});

			this._dots.forEach((dot, i)=>{
				dot.addEventListener('click', ()=>{
					this._stopAutoplay();
					this.go(i);
				});
			});

			// autoplay

			this._speed = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;

			if ( this._speed && ! window.matchMedia('(prefers-reduced-motion: reduce)').matches ) {
				this._startTimer();
				this.addEventListener('pointerenter', ()=>this._clearTimer());
				this.addEventListener('pointerleave', ()=>this._startTimer());
				this.addEventListener('focusin', ()=>this._clearTimer());
				this.addEventListener('focusout', ()=>this._startTimer());
			}

			// theme editor: show the slide whose block is being edited

			if ( window.Shopify && window.Shopify.designMode ) {
				this.addEventListener('shopify:block:select', e=>{
					this._clearTimer();
					const card = e.target.closest('.hero-slider__card');
					if ( card ) this.go(parseInt(card.dataset.slide, 10));
				});
				this.addEventListener('shopify:block:deselect', ()=>this._startTimer());
			}

		}

		disconnectedCallback(){
			this._clearTimer();
			if ( this._onResize ) {
				window.removeEventListener('resize', this._onResize);
				this._onResize = null;
			}
			this._initialized = false;
		}

		_fitClaim(){
			const text = this._claim;
			text.style.fontSize = '';
			let size = parseFloat(window.getComputedStyle(text).fontSize);
			let guard = 48;
			while ( text.scrollWidth > text.clientWidth && size > 6 && guard-- > 0 ) {
				size -= 0.5;
				text.style.fontSize = size + 'px';
			}
		}

		go(index){
			const total = this._cards.length;
			this._index = (index + total) % total;

			this._images.forEach((el, i)=>el.classList.toggle('is-active', i === this._index));
			this._cards.forEach((el, i)=>el.classList.toggle('is-active', i === this._index));
			this._dots.forEach((el, i)=>{
				el.classList.toggle('is-active', i === this._index);
				if ( i === this._index ) {
					el.setAttribute('aria-current', 'true');
				} else {
					el.removeAttribute('aria-current');
				}
			});
		}

		_startTimer(){
			if ( ! this._speed || this._stopped || this._timer ) return;
			this._timer = setInterval(()=>{
				if ( document.hidden ) return;
				this.go(this._index + 1);
			}, this._speed);
		}

		_clearTimer(){
			if ( this._timer ) {
				clearInterval(this._timer);
				this._timer = null;
			}
		}

		_stopAutoplay(){
			this._stopped = true;
			this._clearTimer();
		}

	}

	customElements.define('hero-slider', HeroSlider);

}
